import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Eye, Edit, ChevronRight, ChevronDown, History, AlertCircle, Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ChangeOrderEditDialog from "@/components/ChangeOrderEditDialog";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";

const WORK_STATUS = {
  yet_to_start: { label: "Yet to Start", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  in_progress: { label: "In Progress", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  waiting_for_rfi: { label: "Waiting for RFI", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  released_for_approval: { label: "Released for Approval", cls: "bg-purple-100 text-purple-800 border-purple-200" },
  released_for_fab: { label: "Released for Fab", cls: "bg-green-100 text-green-800 border-green-200" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to conditionally show version history button
const VersionHistoryButton = ({ coId, coNumber, onFetchHistory, coVersionCounts, getVersionCount }) => {
  const [hasMultipleVersions, setHasMultipleVersions] = useState(null);
  
  useEffect(() => {
    // Check version count
    const checkVersions = async () => {
      const count = await getVersionCount(coId, coNumber);
      setHasMultipleVersions(count > 1);
    };
    checkVersions();
  }, [coId, coNumber, getVersionCount]);
  
  // Don't render anything if still loading or only one version
  if (hasMultipleVersions === null || !hasMultipleVersions) {
    return null;
  }
  
  return (
    <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
      size="sm"
      onClick={() => onFetchHistory(coId)}
      title="View Version History"
    >
      <History className="h-4 w-4" />
    </Button>
  );
};

export default function ChangeOrderLog({ user }) {
  const [changeOrders, setChangeOrders] = useState([]);
  const [filteredCOs, setFilteredCOs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterWorkStatus, setFilterWorkStatus] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedCO, setSelectedCO] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [coHistory, setCoHistory] = useState([]);
  
  // Form states
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  
  // Track version counts for each CO
  const [coVersionCounts, setCoVersionCounts] = useState({});

  useEffect(() => {
    fetchChangeOrders();
    fetchProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [changeOrders, searchTerm, filterStatus, filterWorkStatus, filterBranch]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('co-project-dropdown');
      const input = event.target.closest('input[placeholder*="Type to search"]');
      if (dropdown && !dropdown.contains(event.target) && !input) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchChangeOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/change-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChangeOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch change orders:", error);
      toast.error("Failed to load change orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const fetchCOHistory = async (coId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/change-orders/${coId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoHistory(response.data);
      setIsHistoryDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch CO history:", error);
      toast.error("Failed to load version history");
    }
  };

  const coBranches = (co) => (co.branch ? [co.branch] : co.project_branches || []);
  const branchOptions = [...new Set(changeOrders.flatMap(coBranches))].sort();

  const applyFilters = () => {
    let filtered = [...changeOrders];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(co =>
        (co.project_name || "").toLowerCase().includes(term) ||
        (co.project_number || "").toLowerCase().includes(term) ||
        (co.change_order_number || "").toLowerCase().includes(term)
      );
    }
    if (filterStatus !== "all") filtered = filtered.filter(co => co.status === filterStatus);
    if (filterWorkStatus !== "all") filtered = filtered.filter(co => co.status === "approved" && (co.work_completion_status || "yet_to_start") === filterWorkStatus);
    if (filterBranch !== "all") filtered = filtered.filter(co => coBranches(co).includes(filterBranch));
    filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    setFilteredCOs(filtered);
  };

  const isProjectsDeptUser = () =>
    user?.department === "Projects" && ["Team Member", "Team Lead", "Manager"].includes(user?.access_level);
  const canViewCosts = () => ["Management", "Sales", "PMO", "Accounts and Finance"].includes(user?.department);
  const canEditWorkStatus = () =>
    user?.access_level === "Admin" ||
    ["Management", "PMO", "HR", "Human Resources"].includes(user?.department) ||
    (user?.department === "Projects" && ["Manager", "Team Lead"].includes(user?.access_level));

  const handleUpdateWorkStatus = async (co, value) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/projects/${co.project_id}/change-orders/${co.id}/work-completion-status`,
        { work_completion_status: value }, { headers: { Authorization: `Bearer ${token}` } });
      setChangeOrders(prev => prev.map(c => (c.id === co.id ? { ...c, work_completion_status: value } : c)));
      toast.success("Work status updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update work status");
    }
  };

  const openViewDialog = (co) => {
    setSelectedCO(co);
    setIsViewDialogOpen(true);
  };

  const handleEditCO = async (co) => {
    // Fetch the full project details
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/projects/${co.project_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedProject(response.data);
      setSelectedCO(co);
      setIsEditDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch project:", error);
      toast.error("Failed to load project details");
    }
  };

  const handleCreateCO = () => {
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }
    
    // Navigate to project detail page with CO tab open
    const projectUrl = `/projects/${selectedProjectId}?tab=change-orders&action=create`;
    window.location.href = projectUrl;
  };

  const handleEditSuccess = async () => {
    // Refresh change orders after edit
    await fetchChangeOrders();
    
    // Clear version count cache
    setCoVersionCounts({});
  };
  
  const getVersionCount = async (coId, coNumber) => {
    // Check if already cached
    if (coVersionCounts[coId] !== undefined) {
      return coVersionCounts[coId];
    }
    
    // Fetch from API
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/change-orders/${coId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = response.data.length;
      setCoVersionCounts(prev => ({ ...prev, [coId]: count }));
      return count;
    } catch (error) {
      console.error("Failed to get version count:", error);
      return 1; // Default to 1 if error
    }
  };

  const getFilteredProjects = () => {
    if (!projectSearch) return projects;
    const search = projectSearch.toLowerCase();
    return projects.filter(p => 
      (p.project_number || "").toLowerCase().includes(search) ||
      (p.name || "").toLowerCase().includes(search)
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      "pending": "bg-yellow-100 text-yellow-800",
      "approved": "bg-green-100 text-green-800",
      "rejected": "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-md3-surface-container text-md3-on-surface";
  };

  const getTypeColor = (type) => {
    const colors = {
      "Detailing": "bg-blue-100 text-blue-800",
      "Engineering": "bg-purple-100 text-purple-800"
    };
    return colors[type] || "bg-md3-surface-container text-md3-on-surface";
  };

  const canCreateOrEdit = () => {
    const allowedLevels = ["Team Lead", "Manager", "Admin"];
    return (user?.department === "Projects" && allowedLevels.includes(user?.access_level)) ||
           user?.department === "Management";
  };

  const canDelete = () => {
    return (user?.department === "Projects" && user?.access_level === "Admin") ||
           user?.department === "Management";
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-md3-display-small flex items-center gap-2">
              <FileText className="h-8 w-8 text-blue-600" />
              Change Order Log
            </h1>
            <p className="text-md3-on-surface-variant mt-1">Project Change Order Management</p>
          </div>
          <div className="flex gap-2">
            {canCreateOrEdit() && !isProjectsDeptUser() && (
              <Button data-testid="co-log-new-btn" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Change Order
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="md:col-span-2">
                <Label>Project</Label>
                <Input data-testid="co-log-search" className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" placeholder="Search project number, name or CO#..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div>
                <Label>Approval Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="co-log-filter-status" className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Work Status</Label>
                <Select value={filterWorkStatus} onValueChange={setFilterWorkStatus}>
                  <SelectTrigger data-testid="co-log-filter-work-status" className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Work Statuses</SelectItem>
                    {Object.entries(WORK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch</Label>
                <div className="flex gap-2">
                  <Select value={filterBranch} onValueChange={setFilterBranch}>
                    <SelectTrigger data-testid="co-log-filter-branch" className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branchOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" data-testid="co-log-clear-filters" className="border-md3-outline rounded-md3-full"
                    onClick={() => { setSearchTerm(""); setFilterStatus("all"); setFilterWorkStatus("all"); setFilterBranch("all"); }}
                    title="Clear all filters"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-md3-on-surface-variant mt-3" data-testid="co-log-count">Showing {filteredCOs.length} of {changeOrders.length} change orders</p>
          </CardContent>
        </Card>

        {/* Change Order Table */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredCOs.length === 0 ? (
              <div className="text-center py-8 text-md3-on-surface-variant">No change orders found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project #</TableHead>
                      <TableHead>CO #</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Type</TableHead>
                      {!isProjectsDeptUser() && <TableHead>Line Items</TableHead>}
                      <TableHead>Hours</TableHead>
                      {canViewCosts() && <TableHead>Cost</TableHead>}
                      <TableHead>Approval Status</TableHead>
                      <TableHead>Work Status</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Created Date</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCOs.map((co) => {
                        const isLocked = co.is_invoiced || co.is_locked;
                        const approved = co.status === "approved";
                        const hours = approved ? (co.approved_hours || co.time_impact || 0) : (co.time_impact || 0);
                        const cost = approved ? (co.approved_cost || co.cost_impact || 0) : (co.cost_impact || 0);
                        const ws = WORK_STATUS[co.work_completion_status || "yet_to_start"] || WORK_STATUS.yet_to_start;
                        
                        return (
                          <TableRow key={co.id} data-testid={`co-log-row-${co.id}`}>
                            <TableCell className="whitespace-nowrap">
                              <RouterLink to={`/projects/${co.project_id}`} className="text-md3-primary hover:underline" data-testid={`co-log-project-number-${co.id}`}>{co.project_number}</RouterLink>
                            </TableCell>
                            <TableCell className="font-mono font-semibold whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {isLocked && (
                                  <Lock className="h-4 w-4 text-orange-500" title={co.is_invoiced ? "Invoiced" : "Locked"} />
                                )}
                                {co.change_order_number}
                                <span className="text-xs text-md3-on-surface-variant">v{co.version}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-md3-body-small font-medium max-w-xs">{co.project_name}</TableCell>
                            <TableCell>
                              <Badge className={getTypeColor(co.type)}>
                                {co.type}
                              </Badge>
                            </TableCell>
                            {!isProjectsDeptUser() && (
                              <TableCell>
                                <span className="text-md3-body-small font-medium">{co.line_items?.length || 0}</span>
                              </TableCell>
                            )}
                            <TableCell className="whitespace-nowrap" data-testid={`co-log-hours-${co.id}`}>
                              {isProjectsDeptUser() && !approved ? <span className="text-gray-400">-</span> : (
                                <span className="text-md3-body-small">{Number(hours).toFixed(2)}h</span>
                              )}
                            </TableCell>
                            {canViewCosts() && (
                              <TableCell className="whitespace-nowrap" data-testid={`co-log-cost-${co.id}`}>
                                <span className="text-md3-body-small">{Number(cost).toLocaleString()} {co.project_currency || "USD"}</span>
                              </TableCell>
                            )}
                            <TableCell>
                              <Badge className={getStatusColor(co.status)}>
                                {co.status}
                              </Badge>
                            </TableCell>
                            <TableCell data-testid={`co-log-work-status-${co.id}`}>
                              {!approved ? <span className="text-gray-400">-</span> : canEditWorkStatus() ? (
                                <Select value={co.work_completion_status || "yet_to_start"} onValueChange={(v) => handleUpdateWorkStatus(co, v)}>
                                  <SelectTrigger className="h-7 w-48 text-xs bg-white" data-testid={`co-log-work-status-select-${co.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(WORK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className={ws.cls}>{ws.label}</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-md3-body-small">{coBranches(co).join(", ") || '-'}</span>
                            </TableCell>
                            <TableCell className="text-md3-body-small whitespace-nowrap" data-testid={`co-log-created-${co.id}`}>
                              {co.created_at ? format(new Date(co.created_at), "MMM d, yyyy") : '-'}
                            </TableCell>
                            <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                              {co.created_by_name || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                                  size="sm"
                                  onClick={() => openViewDialog(co)}
                                  title="View Change Order"
                                  data-testid={`co-log-view-${co.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {canCreateOrEdit() && !isProjectsDeptUser() && !isLocked && (
                                  <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                                    size="sm"
                                    onClick={() => handleEditCO(co)}
                                    title="Edit Change Order"
                                    data-testid={`co-log-edit-${co.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                <VersionHistoryButton 
                                  coId={co.id} 
                                  coNumber={co.change_order_number}
                                  onFetchHistory={fetchCOHistory}
                                  coVersionCounts={coVersionCounts}
                                  getVersionCount={getVersionCount}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Create New Change Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div>
                <Label>Select Project *</Label>
                <div className="relative">
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Type to search or select project..."
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      const dropdown = document.getElementById('co-project-dropdown');
                      if (dropdown) dropdown.style.display = 'block';
                    }}
                    onFocus={() => {
                      const dropdown = document.getElementById('co-project-dropdown');
                      if (dropdown) dropdown.style.display = 'block';
                    }}
                  />
                  <div
                    id="co-project-dropdown"
                    className="absolute z-50 w-full mt-1 bg-md3-surface-container border border-md3-outline rounded-md3-md shadow-lg max-h-60 overflow-auto hidden"
                    style={{ display: 'none' }}
                  >
                    {getFilteredProjects().length > 0 ? (
                      getFilteredProjects().map(project => (
                        <div
                          key={project.id}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setProjectSearch(`${project.project_number || ''} - ${project.name || ''}`);
                            document.getElementById('co-project-dropdown').style.display = 'none';
                          }}
                        >
                          <div className="font-semibold text-blue-900">{project.project_number || 'N/A'}</div>
                          <div className="text-md3-body-small text-md3-on-surface-variant">{project.name || 'Unnamed Project'}</div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-md3-on-surface-variant text-md3-body-small">No projects found</div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-md3-on-surface-variant mt-1">
                  You will be redirected to the project detail page to create the change order
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setSelectedProjectId("");
                  setProjectSearch("");
                }}
              >
                Cancel
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleCreateCO}>
                Continue to Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-md3-headline-small text-md3-on-surface font-semibold">
                Change Order Details - {selectedCO?.change_order_number} v{selectedCO?.version}
                {selectedCO?.is_invoiced && (
                  <Badge variant="outline" className="ml-2 border-orange-500 text-orange-700">
                    <Lock className="h-3 w-3 mr-1" />
                    Invoiced
                  </Badge>
                )}
                {selectedCO?.is_locked && !selectedCO?.is_invoiced && (
                  <Badge variant="outline" className="ml-2 border-orange-500 text-orange-700">
                    <Lock className="h-3 w-3 mr-1" />
                    Locked
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedCO && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Project</Label>
                    <p className="font-medium">
                      {selectedCO.project_number} - {selectedCO.project_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Type</Label>
                    <div>
                      <Badge className={getTypeColor(selectedCO.type)}>
                        {selectedCO.type}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Status</Label>
                    <div>
                      <Badge className={getStatusColor(selectedCO.status)}>
                        {selectedCO.status}
                      </Badge>
                    </div>
                  </div>
                  {coBranches(selectedCO).length > 0 && (
                    <div>
                      <Label className="text-md3-on-surface-variant ">Branch</Label>
                      <p className="mt-1">{coBranches(selectedCO).join(", ")}</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(!isProjectsDeptUser() || selectedCO.status === "approved") && (
                    <div>
                      <Label className="text-md3-on-surface-variant ">{selectedCO.status === "approved" ? "Approved Hours" : "Time Impact"}</Label>
                      <p className="font-medium text-lg" data-testid="co-view-hours">
                        {Number(selectedCO.status === "approved" ? (selectedCO.approved_hours || selectedCO.time_impact || 0) : (selectedCO.time_impact || 0)).toFixed(2)} hours
                      </p>
                    </div>
                  )}
                  {canViewCosts() && (
                    <div>
                      <Label className="text-md3-on-surface-variant ">Cost Impact</Label>
                      <p className="font-medium text-lg">{selectedCO.cost_impact?.toLocaleString() || '0'} {selectedCO.project_currency || "USD"}</p>
                    </div>
                  )}
                </div>
                
                {/* Line Items */}
                <div>
                  <Label className="text-md3-body-medium font-semibold mb-2 block ">
                    Line Items ({selectedCO.line_items?.length || 0})
                  </Label>
                  {selectedCO.line_items && selectedCO.line_items.length > 0 ? (
                    <div className="border rounded-md3-lg overflow-hidden">
                      <Table className="bg-md3-surface-container rounded-md3-md">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Description</TableHead>
                            <TableHead>Element</TableHead>
                            <TableHead>Qty</TableHead>
                            <TableHead>Action</TableHead>
                            {!isProjectsDeptUser() && <TableHead>Hours</TableHead>}
                            {canViewCosts() && <TableHead>Cost</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedCO.line_items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="max-w-xs">{item.description}</TableCell>
                              <TableCell>{item.element_name || '-'}</TableCell>
                              <TableCell>{item.element_quantity || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{item.action}</Badge>
                              </TableCell>
                              {!isProjectsDeptUser() && <TableCell>{item.hours?.toFixed(2) || '0.00'}h</TableCell>}
                              {canViewCosts() && <TableCell>{item.cost?.toLocaleString() || '0'}</TableCell>}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-md3-on-surface-variant text-md3-body-small">No line items</p>
                  )}
                </div>

                {selectedCO.hyperlink && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Document Link</Label>
                    <a
                      href={selectedCO.hyperlink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline block mt-1"
                    >
                      {selectedCO.hyperlink}
                    </a>
                  </div>
                )}
                
                {selectedCO.remarks && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Remarks</Label>
                    <p className="mt-1">{selectedCO.remarks}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-md3-body-small text-md3-on-surface-variant">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created By</Label>
                    <p>{selectedCO.created_by_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created At</Label>
                    <p>{new Date(selectedCO.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* Edit Dialog - Shared Component */}
        <ChangeOrderEditDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          changeOrder={selectedCO}
          project={selectedProject}
          onSuccess={handleEditSuccess}
          editMode={true}
          canViewCosts={canViewCosts}
        />

        {/* History Dialog */}
        <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Version History</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {coHistory.map((co, index) => {
                const colors = ['bg-blue-50/50 border-blue-200', 'bg-green-50/50 border-green-200', 'bg-purple-50/50 border-purple-200', 'bg-amber-50/50 border-amber-200', 'bg-pink-50/50 border-pink-200', 'bg-cyan-50/50 border-cyan-200'];
                return (
                <div key={co.id} className={`border rounded-md3-lg p-4 ${colors[index % colors.length]}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{co.change_order_number}</span>
                      <Badge variant={index === 0 ? "default" : "outline"}>v{co.version}</Badge>
                      <Badge className={getStatusColor(co.status)}>{co.status}</Badge>
                      {index === 0 && (
                        <Badge variant="default">Latest</Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    {(!isProjectsDeptUser() || co.status === "approved") && (
                      <div>
                        <span className="text-md3-on-surface-variant">Time Impact:</span> <span className="font-medium">{co.time_impact?.toFixed(2)}h</span>
                      </div>
                    )}
                    {canViewCosts() && (
                      <div>
                        <span className="text-md3-on-surface-variant">Cost Impact:</span> <span className="font-medium">{co.cost_impact?.toLocaleString()}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-md3-on-surface-variant">Line Items:</span> <span className="font-medium">{co.line_items?.length || 0}</span>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Created:</span> <span className="font-medium">{new Date(co.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {co.remarks && (
                    <div className="mt-2 text-md3-body-small">
                      <span className="text-md3-on-surface-variant">Remarks:</span> <span>{co.remarks}</span>
                    </div>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedCO(co);
                        setIsHistoryDialogOpen(false);
                        setIsViewDialogOpen(true);
                      }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Details
                    </Button>
                  </div>
                </div>
              );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
